import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  LuActivity,
  LuArrowRight,
  LuClock,
  LuCheckCircle2,
  LuSmartphone,
} from 'react-icons/lu';
import { NAV_ITEM_ICONS, NAV_ITEMS, BREADCRUMB_LABELS } from '@/constant/layout/layout.constant';
import { AlertLevelIcon, LEVEL_COLOR } from '@/pages/Alert/AlertLevelIcon';
import {
  getModifiedDashboarddata,
  getVdiTransactionCount,
  getDashboardLoading,
} from '@/store/selectors/dashboard.selector';
import {
  getVdiTransactionData,
  getSmartCoolerProductData,
  getAlertData,
  clearDashboardData,
} from '@/store/slices/dashboard.slice';
import { getOperators } from '@/store/slices/operator.slice';
import { getProviders } from '@/store/slices/provider.slice';
import { getAlerts } from '@/store/slices/alert.slice';
import { getProviderList } from '@/store/selectors/providers.selector';
import { getOperatorDetail } from '@/store/selectors/operator.selector';
import { getAlertsList } from '@/store/selectors/alerts.selector';
import { VDI_VERSION } from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import { DASHBOARD_PROPS } from '@/constant/dashboard/dashboard.constant';
import { titleCaseString } from '@/utils/stringFormatting';
import {
  getDefaultDashboardRange,
  getDateTime,
  getSelectedTimeZone,
} from '@/utils/dateTime';
import { useDateFilter } from '@/context/dateFilter.context';
import { useTheme } from '@/context/theme.context';
import { pagination } from '@/constant/global/global.constant';
import {
  DashboardStatCard,
  DashboardChart,
} from '@/components/organism/Dashboard';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { IconBadge } from '@/components/atoms/IconBadge';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { Text, Caption, Heading } from '@/components';
import { APP_BG, APP_BORDER, APP_HEX } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { DASHBOARD_TEST_IDS as TID } from '@/constant/testIds/dashboard.testIds';
import {
  DashSkStyle,
  StatSkeleton,
  ChartSkeleton,
  AlertSectionSkeleton,
} from './DashboardSkeleton';
import { ROUTE_CONFIG } from '@/config/Routes/routes.config';


const formatAlertType = (type: string) =>
  (type ?? '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const dispatch = useDispatch();
  const { filterValues } = useDateFilter();
  const { colors } = useTheme();

  const data = useSelector(getModifiedDashboarddata, shallowEqual);
  const providerData = useSelector(getProviderList, shallowEqual);
  const operatorData = useSelector(getOperatorDetail, shallowEqual);
  const VdiCount = useSelector(getVdiTransactionCount);
  const alertsData = useSelector(getAlertsList, shallowEqual);
  const loading = useSelector(getDashboardLoading, shallowEqual);

  const isChartsLoading = loading?.vdiTransactions;
  const isProductsLoading = loading?.smartCoolerProducts;
  const isAlertsLoading = loading?.alerts;

  const selectedTimezone = getSelectedTimeZone();

  const deferredFilterValues = filterValues;

  const areDatesCleared = useMemo(
    () => !deferredFilterValues?.startDate && !deferredFilterValues?.endDate,
    [deferredFilterValues?.startDate, deferredFilterValues?.endDate],
  );

  useEffect(() => {
    return () => {
      dispatch(clearDashboardData());
    };
  }, [dispatch]);

  const defaultDates = useMemo(
    () => getDefaultDashboardRange(selectedTimezone),
    [selectedTimezone],
  );

  const finalDates = useMemo(
    () => ({
      startDate: !areDatesCleared
        ? deferredFilterValues.startDate
        : defaultDates.startDate,
      endDate: !areDatesCleared
        ? deferredFilterValues.endDate
        : defaultDates.endDate,
    }),
    [
      areDatesCleared,
      deferredFilterValues.startDate,
      deferredFilterValues.endDate,
      defaultDates,
    ],
  );

  const params = useMemo(
    () => ({
      ...finalDates,
      ...(selectedTimezone && { selectedTimezone }),
    }),
    [finalDates, selectedTimezone],
  );

  const fetchTransaction = useCallback(
    () =>
      dispatch(
        getVdiTransactionData({ ...params, version: VDI_VERSION[1].id }),
      ),
    [dispatch, finalDates.startDate, finalDates.endDate, selectedTimezone],
  );
  const fetchSCProducts = useCallback(
    () => dispatch(getSmartCoolerProductData({ ...finalDates })),
    [dispatch, finalDates.startDate, finalDates.endDate],
  );
  const fetchAlertDetail = useCallback(
    () => dispatch(getAlertData({ ...finalDates })),
    [dispatch, finalDates.startDate, finalDates.endDate],
  );

  useEffect(() => {
    fetchTransaction();
    fetchSCProducts();
    fetchAlertDetail();
  }, [fetchTransaction, fetchSCProducts, fetchAlertDetail]);

  // one-time fetches
  useEffect(() => {
    dispatch(getOperators(pagination));
    dispatch(getProviders(pagination));
    dispatch(getAlerts({ ...pagination, limit: 5 }));
  }, [dispatch]);

  // derived values
  const scTotal = data?.data?.smartCoolerProducts?.total ?? 0;
  const providersCount = providerData?.providers?.pagination?.totalRecords ?? 0;
  const operatorsCount = operatorData?.operators?.pagination?.totalRecords ?? 0;
  const recentAlerts = useMemo(
    () => (alertsData?.data ?? []).slice(0, 5),
    [alertsData],
  );

  const chartTypes = useMemo(() => {
    const types: string[] = data?.data?.type || [];
    const PREFERRED_ORDER = [
      'mmsSales',
      'mmsMarkets',
      'mmsProducts',
      'mmsDevices',
    ];
    return [...types].sort((a, b) => {
      const ai = PREFERRED_ORDER.indexOf(a);
      const bi = PREFERRED_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [data?.data?.type]);
  const chartData = useMemo(
    () => (data?.data?.vdiTransactions || {}) as Record<string, any>,
    [data],
  );
  const [heroType, ...restTypes] = chartTypes as string[];

  const CHART_NAV_MAP: Record<string, { navItem: keyof typeof NAV_ITEM_ICONS; label: string; href: string }> = {
    mmsSales: { navItem: NAV_ITEMS.SALES, label: BREADCRUMB_LABELS.SALES, href: '/sales' },
    mmsMarkets: { navItem: NAV_ITEMS.MARKETS, label: BREADCRUMB_LABELS.MARKETS, href: '/markets' },
    mmsProducts: { navItem: NAV_ITEMS.PRODUCTS, label: BREADCRUMB_LABELS.PRODUCTS, href: '/products' },
    mmsDevices: { navItem: NAV_ITEMS.DEVICES, label: BREADCRUMB_LABELS.DEVICES, href: '/devices' },
  };
  const getChartMeta = (key: string) => ({
    title: CHART_NAV_MAP[key]?.label ?? titleCaseString(key),
    icon: CHART_NAV_MAP[key]
      ? React.createElement(NAV_ITEM_ICONS[CHART_NAV_MAP[key].navItem], { className: 'w-4 h-4' })
      : undefined,
    href: CHART_NAV_MAP[key]?.href,
  });

  const dateLabel =
    finalDates.startDate && finalDates.endDate
      ? `${finalDates.startDate}  →  ${finalDates.endDate}`
      : DASHBOARD_PROPS.PAGE.DATE_LABEL_DEFAULT;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="flex flex-col min-h-full">
      <div
        data-testid={tid(TID.PAGE)}
        className="flex flex-col w-full gap-5 pb-8"
        style={{
          contain: 'layout style paint',
        }}
        ref={scrollContainerRef}
      >
        <DashSkStyle />

        {/* ── Page Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div>
            <div className="flex items-center gap-2">
              <Heading size="5xl" color="default" tracking="tight">
                {DASHBOARD_PROPS.PAGE.TITLE}
              </Heading>
            </div>
            <Caption color="muted" className="mt-0.5" data-testid={tid(TID.DATE_LABEL)}>
              {dateLabel}
            </Caption>
          </div>
          <LuActivity className="w-4 h-4 text-gray-300 dark:text-gray-600" />
        </div>

        {/* ── Stat Cards ──────────────────────────────────────────────────────── */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isProductsLoading ? (
            <StatSkeleton delay={0} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_SMART_COOLER_PRODUCTS)}
              title={BREADCRUMB_LABELS.SMART_COOLER_PRODUCTS}
              value={scTotal}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.SMART_COOLER_PRODUCTS]}
              variant={1}
              href={`/${ROUTE_CONFIG.SMART_COOLER_PRODUCTS.path}`}
            />
          )}

          {isChartsLoading ? (
            <StatSkeleton delay={0.1} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_VDI_TRANSACTIONS)}
              title={BREADCRUMB_LABELS.VDI_TRANSACTIONS}
              value={(VdiCount as number) ?? 0}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.VDI_TRANSACTIONS]}
              variant={2}
              href={`/${ROUTE_CONFIG.VDI_TRANSACTIONS.path}`}
            />
          )}

          {operatorData?.loading ? (
            <StatSkeleton delay={0.2} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_OPERATORS)}
              title={BREADCRUMB_LABELS.OPERATORS}
              value={operatorsCount}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.OPERATORS]}
              variant={3}
              href={`/${ROUTE_CONFIG.OPERATORS.path}`}
            />
          )}
          {providerData?.loading ? (
            <StatSkeleton delay={0.3} />
          ) : (
            <DashboardStatCard
              testId={tid(TID.STAT_PROVIDERS)}
              title={BREADCRUMB_LABELS.PROVIDERS}
              value={providersCount}
              icon={NAV_ITEM_ICONS[NAV_ITEMS.PROVIDERS]}
              variant={4}
              href={`/${ROUTE_CONFIG.PROVIDERS.path}`}
            />
          )}
        </div>

        {/* ── Hero Chart ──────────────────────────────────────────────────────── */}
        <div
          style={{
            contain: 'layout paint style',
          }}
        >
          {isChartsLoading ? (
            <ChartSkeleton height={400} delay={0} barCount={24} />
          ) : heroType && chartData[heroType] ? (
            <DashboardChart
              testId={tid(TID.HERO_CHART)}
              chartKey={heroType}
              title={getChartMeta(heroType).title}
              icon={getChartMeta(heroType).icon}
              subtitle={DASHBOARD_PROPS.CHARTS.DEFAULT_SUBTITLE}
              data={chartData[heroType]}
              height={300}
              href={getChartMeta(heroType).href}
            />
          ) : null}
        </div>

        {/* ── Chart Grid + Recent Alerts ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {isChartsLoading ? (
            /* Show 3 chart skeletons filling the grid while data loads */
            <>
              <ChartSkeleton height={288} delay={0} />
              <ChartSkeleton height={288} delay={0.1} />
              <ChartSkeleton height={288} delay={0.2} />
            </>
          ) : (
            restTypes?.map((item: string, idx: number) =>
              chartData[item] ? (
                <div
                  key={item}
                  style={{
                    contain: 'layout paint style',
                    animation: 'dashChartIn 0.22s ease both',
                    animationDelay: `${idx * 80}ms`,
                  }}
                >
                  <DashboardChart
                    testId={tid(TID.CHART(item))}
                    chartKey={item}
                    title={getChartMeta(item).title}
                    icon={getChartMeta(item).icon}
                    subtitle={DASHBOARD_PROPS.CHARTS.DEFAULT_SUBTITLE}
                    data={chartData[item]}
                    height={250}
                    href={getChartMeta(item).href}
                  />
                </div>
              ) : null,
            )
          )}

          {isChartsLoading || isAlertsLoading ? (
            <ChartSkeleton height={288} delay={0.15} />
          ) : data?.data?.alerts ? (
            <div
              style={{
                contain: 'layout paint style',
                animation: 'dashChartIn 0.22s ease both',
                animationDelay: `${restTypes.length * 80}ms`,
              }}
            >
              <DashboardChart
                testId={tid(TID.ALERTS_CHART)}
                chartKey="alerts"
                title={BREADCRUMB_LABELS.ALERTS}
                icon={React.createElement(NAV_ITEM_ICONS[NAV_ITEMS.ALERTS], { className: 'w-4 h-4' })}
                subtitle={DASHBOARD_PROPS.CHARTS.ALERTS_SUBTITLE}
                data={data.data.alerts}
                height={250}
                href={`/${ROUTE_CONFIG.SMART_COOLER_ALERTS.path}`}
              />
            </div>
          ) : null}

          {/* ── Recent Alerts ── spans 2 cols */}
          <div className="md:col-span-2 xl:col-span-2">
            {isAlertsLoading ? (
              <AlertSectionSkeleton rows={5} delay={0} />
            ) : (
              <SectionCard
                testId={tid(TID.RECENT_ALERTS_SECTION)}
                className="h-full"
                icon={
                  <span className="relative inline-flex">
                    {React.createElement(NAV_ITEM_ICONS[NAV_ITEMS.ALERTS], { size: 14 })}
                    {recentAlerts.some(
                      (a: any) =>
                        a?.status?.toUpperCase() ===
                        DASHBOARD_PROPS.RECENT_ALERTS.STATUS_OPEN,
                    ) && (
                        <span
                          className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500 border ${APP_BORDER.dashDot} animate-pulse`}
                        />
                      )}
                  </span>
                }
                title={DASHBOARD_PROPS.RECENT_ALERTS.TITLE}
                subtitle={`${DASHBOARD_PROPS.RECENT_ALERTS.SUBTITLE_PREFIX} ${recentAlerts.length} ${DASHBOARD_PROPS.RECENT_ALERTS.SUBTITLE_SUFFIX}`}
                headerRight={
                  <Link
                    to={`/${ROUTE_CONFIG.SMART_COOLER_ALERTS.path}`}
                    className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold transition-opacity hover:opacity-60"
                    style={{ color: colors.primary }}
                  >
                    {DASHBOARD_PROPS.RECENT_ALERTS.VIEW_ALL}{' '}
                    <LuArrowRight size={9} />
                  </Link>
                }
                headerClassName={APP_BG.canvas}
                bodyBg={APP_BG.base}
                bodyClassName=""
              >
                {recentAlerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-6 gap-2">
                    <LuCheckCircle2
                      size={16}
                      className="text-gray-200 dark:text-gray-700"
                    />
                    <Caption weight="medium" color="faint">
                      {DASHBOARD_PROPS.RECENT_ALERTS.EMPTY}
                    </Caption>
                  </div>
                ) : (
                  <div className="flex flex-col divide-y divide-gray-50 dark:divide-white/[0.035]">
                    {recentAlerts.map((alert: any, idx: number) => {
                      const level = (alert?.level ?? 'INFO').toUpperCase();
                      const status = (alert?.status ?? '').toUpperCase();
                      const levelColor = LEVEL_COLOR[level] ?? APP_HEX.muted;

                      return (
                        <div
                          key={alert?.alertId ?? idx}
                          data-testid={TID.ALERT_ROW(idx)}
                          className="group grid grid-cols-[1fr_auto_auto] gap-x-2.5 items-center px-4 py-2.5 hover:bg-gray-50/80 dark:hover:bg-white/[0.02] transition-colors cursor-default"
                        >
                          {/* Left: icon + text */}
                          <div className="flex items-center gap-2 min-w-0">
                            <IconBadge
                              icon={
                                <AlertLevelIcon
                                  level={level}
                                  className="w-[18px] h-[18px]"
                                />
                              }
                              size="sm"
                              variant="custom"
                              customStyle={{ background: `${levelColor}18` }}
                              shadow={false}
                            />
                            <div className="min-w-0 flex flex-col justify-center">
                              <Text
                                as="p"
                                size="md"
                                weight="semibold"
                                color="soft"
                                truncate
                                className="leading-none"
                              >
                                {formatAlertType(alert?.type)}
                              </Text>
                              <Caption
                                size="xs"
                                color="muted"
                                truncate
                                className="flex items-center gap-1 leading-none mt-[3px]"
                              >
                                <LuClock size={7} className="shrink-0" />
                                {alert?.triggeredOn
                                  ? getDateTime(alert.triggeredOn)
                                  : '—'}
                                {alert?.deviceId && (
                                  <>
                                    <span className="opacity-30">·</span>
                                    <LuSmartphone size={7} className="shrink-0" />
                                    <span className="truncate max-w-[55px]">
                                      {alert.deviceId}
                                    </span>
                                  </>
                                )}
                              </Caption>
                            </div>
                          </div>

                          {/* Level pill */}
                          <PillCell
                            value={level}
                            icon={
                              <AlertLevelIcon level={level} className="w-3 h-3" />
                            }
                            customStyle={
                              levelColor
                                ? {
                                  backgroundColor: `${levelColor}18`,
                                  color: levelColor,
                                }
                                : undefined
                            }
                            config={
                              levelColor
                                ? { bg: '', text: '' }
                                : { bg: 'bg-gray-100', text: 'text-gray-500' }
                            }
                          />

                          {/* Status pill */}
                          <PillCell value={status} preset="alertStatus" dot />
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </div>
  </div>
    </div>
  );
};

export default React.memo(Dashboard);
